// hooks/useAcademicData.js (UPDATED with term support)

import { useState, useEffect, useCallback } from 'react';
import api from '../../../api';

export const useAcademicData = (tenantId, token) => {
  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [isSemesterBased, setIsSemesterBased] = useState(false);
  const [semesters, setSemesters] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState('');
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  
  // Terms for non-semester-based academic years
  const [terms, setTerms] = useState([]);
  const [selectedTermId, setSelectedTermId] = useState('');
  const [selectedTerm, setSelectedTerm] = useState(null);

  console.log('[useAcademicData] Hook initialized with tenantId:', tenantId);

  // Fetch terms for selected academic year (non-semester-based)
  const fetchTerms = useCallback(async () => {
    if (!selectedAcademicYear || !tenantId || !token) {
      console.log('[fetchTerms] Missing required data:', { selectedAcademicYear, tenantId, token: !!token });
      return;
    }
    
    console.log(`[fetchTerms] Fetching terms for academic year ${selectedAcademicYear}`);
    
    try {
      const response = await api.get(
        `/api/academic-year/${selectedAcademicYear}/terms?tenantId=${tenantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('[fetchTerms] Response:', response.data);
      setTerms(response.data.terms || []);
      
      // Only set default if not already selected
      if (response.data.terms?.length > 0 && !selectedTermId) {
        console.log('[fetchTerms] Setting default term to:', response.data.terms[0]._id);
        setSelectedTermId(response.data.terms[0]._id);
        setSelectedTerm(response.data.terms[0]);
      }
    } catch (err) {
      console.error('[fetchTerms] Error:', err);
      setTerms([]);
    }
  }, [selectedAcademicYear, tenantId, token, selectedTermId]);

  // Fetch academic years
  const fetchAcademicYears = useCallback(async () => {
    if (!tenantId || !token) {
      console.log('[fetchAcademicYears] Missing tenantId or token');
      return;
    }
    
    console.log('[fetchAcademicYears] Fetching academic years');
    
    try {
      const response = await api.get(
        `/api/academic-year/all-active?tenantId=${tenantId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('[fetchAcademicYears] Response:', response.data);
      const years = response.data.academicYears || [];
      setAcademicYears(years);
      
      // Only set default if not already selected
      if (years.length > 0 && !selectedAcademicYear) {
        console.log('[fetchAcademicYears] Setting default academic year to:', years[0]._id);
        setSelectedAcademicYear(years[0]._id);
        setIsSemesterBased(years[0].isSemesterBased || false);
      }
    } catch (err) {
      console.error('[fetchAcademicYears] Error:', err);
      setAcademicYears([]);
    }
  }, [tenantId, token, selectedAcademicYear]);

  // Fetch semesters for selected academic year
  const fetchSemesters = useCallback(async () => {
    if (!selectedAcademicYear || !token) {
      console.log('[fetchSemesters] Missing selectedAcademicYear or token');
      return;
    }
    
    console.log(`[fetchSemesters] Fetching semesters for academic year ${selectedAcademicYear}`);
    
    try {
      const response = await api.get(
        `/api/classManager/semesters/${selectedAcademicYear}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('[fetchSemesters] Response:', response.data);
      setSemesters(response.data.semesters || []);
      
      // Only set default if not already selected
      if (response.data.semesters?.length > 0 && !selectedSemester) {
        console.log('[fetchSemesters] Setting default semester to:', response.data.semesters[0]._id);
        setSelectedSemester(response.data.semesters[0]._id);
      }
    } catch (err) {
      console.error('[fetchSemesters] Error:', err);
      setSemesters([]);
    }
  }, [selectedAcademicYear, token, selectedSemester]);

  // Fetch classes for selected semester or academic year
  const fetchClasses = useCallback(async () => {
    if (!token) {
      console.log('[fetchClasses] Missing token');
      return;
    }
    
    console.log('[fetchClasses] Fetching classes', {
      isSemesterBased,
      selectedSemester,
      selectedAcademicYear
    });
    
    try {
      if (isSemesterBased && selectedSemester) {
        console.log(`[fetchClasses] Fetching classes for semester ${selectedSemester}`);
        const response = await api.get(
          `/api/classManager/classes-for-semester/${selectedSemester}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        console.log('[fetchClasses] Response:', response.data);
        setClasses(response.data.classes || []);
        // Only set default if not already selected
        if (response.data.classes?.length > 0 && !selectedClassId) {
          console.log('[fetchClasses] Setting default class to:', response.data.classes[0]._id);
          setSelectedClassId(response.data.classes[0]._id);
        }
      } else if (!isSemesterBased && selectedTermId) {
        console.log(`[fetchClasses] Fetching classes for termId ${selectedTermId}`);
        const response = await api.get(
          `/api/classManager/classes-for-term/${selectedTermId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        const classesData = response.data?.classes || response.data || [];
        console.log('[fetchClasses] Response:', classesData);
        setClasses(classesData);
        // Only set default if not already selected
        if (classesData.length > 0 && !selectedClassId) {
          console.log('[fetchClasses] Setting default class to:', classesData[0]._id);
          setSelectedClassId(classesData[0]._id);
        }
      } else if (!isSemesterBased && selectedAcademicYear) {
        console.log(`[fetchClasses] Fetching classes for academic year ${selectedAcademicYear}`);
        const response = await api.get(
          `/api/classManager/classes-for-academic-year/${selectedAcademicYear}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        console.log('[fetchClasses] Response:', response.data);
        setClasses(response.data.classes || []);
        // Only set default if not already selected
        if (response.data.classes?.length > 0 && !selectedClassId) {
          console.log('[fetchClasses] Setting default class to:', response.data.classes[0]._id);
          setSelectedClassId(response.data.classes[0]._id);
        }
      } else {
        console.log('[fetchClasses] No valid parameters, clearing classes');
        setClasses([]);
      }
    } catch (err) {
      console.error('[fetchClasses] Error:', err);
      setClasses([]);
    }
  }, [isSemesterBased, selectedSemester, selectedAcademicYear, token, selectedClassId, selectedTermId]);

  // When academic year changes, check if it's semester-based and fetch semesters or classes
  useEffect(() => {
    console.log('[useEffect] Academic year changed:', selectedAcademicYear);
    
    if (!selectedAcademicYear) return;
    
    const yearObj = academicYears.find(y => y._id === selectedAcademicYear);
    console.log('[useEffect] Year object:', yearObj);
    
    if (yearObj?.isSemesterBased) {
      console.log('[useEffect] Year is semester-based');
      setIsSemesterBased(true);
      fetchSemesters();
    } else {
      console.log('[useEffect] Year is term-based');
      setIsSemesterBased(false);
      setSemesters([]);
      setSelectedSemester('');
      fetchTerms();
      fetchClasses();
    }
    // eslint-disable-next-line
  }, [selectedAcademicYear, academicYears]);

  // When semester changes, fetch classes if semester-based
  useEffect(() => {
    console.log('[useEffect] Semester changed:', selectedSemester, 'isSemesterBased:', isSemesterBased);
    
    if (isSemesterBased && selectedSemester) {
      fetchClasses();
    }
    // eslint-disable-next-line
  }, [isSemesterBased, selectedSemester]);

  // When term changes, fetch classes
  useEffect(() => {
    console.log('[useEffect] Term changed:', selectedTermId);
    
    if (!isSemesterBased && selectedTermId) {
      // Find the term object
      const term = terms.find(t => t._id === selectedTermId);
      setSelectedTerm(term);
      fetchClasses();
    }
    // eslint-disable-next-line
  }, [selectedTermId, isSemesterBased]);

  // Fetch academic years on mount
  useEffect(() => {
    console.log('[useEffect] Initial mount, fetching academic years');
    fetchAcademicYears();
    // eslint-disable-next-line
  }, []);

  return {
    academicYears,
    selectedAcademicYear,
    setSelectedAcademicYear,
    isSemesterBased,
    semesters,
    selectedSemester,
    setSelectedSemester,
    classes,
    selectedClassId,
    setSelectedClassId,
    fetchAcademicData: fetchAcademicYears,
    terms,
    selectedTermId,
    setSelectedTermId,
    selectedTerm,
    fetchTerms
  };
};